import React from 'react';
import { Guide, NewsPost, UpdateNote } from '../../../types/EducationHub';
import styles from '../../../styles/EducationHub.module.css';

interface ContentMetadataProps {
  content: Guide | NewsPost | UpdateNote;
  contentType: 'guide' | 'news' | 'update';
}

const getDifficultyColor = (difficulty: string) => {
  switch (difficulty) {
    case 'Beginner': return '#22c55e';
    case 'Intermediate': return '#f59e0b';
    case 'Advanced': return '#ef4444';
    default: return '#6b7280';
  }
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'Critical': return '#dc2626';
    case 'High': return '#ea580c';
    case 'Medium': return '#d97706';
    case 'Low': return '#16a34a';
    default: return '#6b7280';
  }
};

const getTypeIcon = (type: string) => {
  switch (type) {
    case 'Major': return '🚀';
    case 'Minor': return '✨';
    case 'Patch': return '🔧';
    case 'Hotfix': return '🩹';
    default: return '📦';
  }
};

const formatDate = (date: Date | string) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const ContentMetadata: React.FC<ContentMetadataProps> = ({ content, contentType }) => {
  if (!content) return null;

  switch (contentType) {
    case 'guide': {
      const guide = content as Guide;
      return (
        <div className={styles.metadata}>
          <div className={styles.metadataItem}>
            <span className={styles.metadataLabel}>Category:</span>
            <span className={styles.metadataValue}>📂 {guide.category}</span>
          </div>
          <div className={styles.metadataItem}>
            <span className={styles.metadataLabel}>Difficulty:</span>
            <span 
              className={styles.metadataValue}
              style={{ color: getDifficultyColor(guide.difficulty) }}
            >
              🎯 {guide.difficulty}
            </span>
          </div>
          {guide.estimatedDuration && (
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>Duration:</span>
              <span className={styles.metadataValue}>⏱️ {guide.estimatedDuration} min</span>
            </div>
          )}
          <div className={styles.metadataItem}>
            <span className={styles.metadataLabel}>Published:</span>
            <span className={styles.metadataValue}>📅 {formatDate(guide.publishedAt || guide.createdAt)}</span>
          </div>
        </div>
      );
    }
    case 'news': {
      const newsPost = content as NewsPost;
      return (
        <div className={styles.metadata}>
          <div className={styles.metadataItem}>
            <span className={styles.metadataLabel}>Category:</span>
            <span className={styles.metadataValue}>📂 {newsPost.category}</span>
          </div>
          <div className={styles.metadataItem}>
            <span className={styles.metadataLabel}>Priority:</span>
            <span 
              className={styles.metadataValue}
              style={{ color: getPriorityColor(newsPost.priority) }}
            >
              🎯 {newsPost.priority}
            </span>
          </div>
          <div className={styles.metadataItem}>
            <span className={styles.metadataLabel}>Published:</span>
            <span className={styles.metadataValue}>📅 {formatDate(newsPost.publishedAt || newsPost.createdAt)}</span>
          </div>
          <div className={styles.metadataItem}>
            <span className={styles.metadataLabel}>Views:</span>
            <span className={styles.metadataValue}>👁️ {newsPost.viewCount}</span>
          </div>
          {newsPost.expiresAt && new Date(newsPost.expiresAt) > new Date() && (
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>Expires:</span>
              <span className={styles.metadataValue} style={{ color: '#d97706' }}>⏰ {formatDate(newsPost.expiresAt)}</span>
            </div>
          )}
        </div>
      );
    }
    case 'update': {
      const update = content as UpdateNote;
      return (
        <div className={styles.metadata}>
          <div className={styles.metadataItem}>
            <span className={styles.metadataLabel}>Version:</span>
            <span className={styles.metadataValue}>📦 v{update.version}</span>
          </div>
          <div className={styles.metadataItem}>
            <span className={styles.metadataLabel}>Type:</span>
            <span className={styles.metadataValue}>{getTypeIcon(update.type)} {update.type}</span>
          </div>
          <div className={styles.metadataItem}>
            <span className={styles.metadataLabel}>Category:</span>
            <span className={styles.metadataValue}>📂 {update.category}</span>
          </div>
          <div className={styles.metadataItem}>
            <span className={styles.metadataLabel}>Priority:</span>
            <span 
              className={styles.metadataValue}
              style={{ color: getPriorityColor(update.priority) }}
            >
              🎯 {update.priority}
            </span>
          </div>
          <div className={styles.metadataItem}>
            <span className={styles.metadataLabel}>Released:</span>
            <span className={styles.metadataValue}>📅 {formatDate(update.releaseDate)}</span>
          </div>
          <div className={styles.metadataItem}>
            <span className={styles.metadataLabel}>Views:</span>
            <span className={styles.metadataValue}>👁️ {update.viewCount}</span>
          </div>
        </div>
      );
    }
    default:
      return null;
  }
};

export default ContentMetadata;
